import { assert, NormalizedNumber } from '@sparkdotfi/common-universal'
import { useSuspenseQueries } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { useEffect } from 'react'
import { mainnet } from 'viem/chains'
import { useChainId, useConfig, useSwitchChain } from 'wagmi'

import {
  convertPointsRewardQueryFunction,
  convertPointsRewardQueryKey,
} from '../dialogs/convert-points-dialog/logic/convertPointsRewardQueryOptions'
import { useAppendBenmoRefCode } from './useAppendBenmoRefCode'
import { getChainConfigEntry } from '@/config/chain'
import { trackAction } from '@/domain/analytics/cookie3'
import { useConnectModal } from '@/domain/hooks/wallet'
import { useSandbox } from '@/domain/sandbox/useSandbox'
import { useTermsOfService } from '@/domain/state/compliance'
import { useCloseDialog, useOpenDialog, useOpenedDialog } from '@/domain/state/dialogs/hooks'
import { useVisibility } from '@/domain/state/visibility'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { benmoInviteCode, sparkPointsOnboardingDialogId } from '@/features/points/consts'
import { convertPointsDialogConfig } from '@/features/points/dialogs/convert-points-dialog/ConvertPointsDialog'
import { pointsOnboardingDialogConfig } from '@/features/points/dialogs/points-onboarding-dialog/PointsOnboardingDialog'
import { referralShareDialogConfig } from '@/features/points/dialogs/referral-share-dialog/ReferralShareDialog'
import { resolveActivePointsSeason, selectSeasonValue } from '@/features/points/helpers'
import { PointsData, pointsDataQueryFunction, pointsDataQueryKey } from '@/features/points/logic/useSparkPointsData'
import { useOpenStakeWithDelegateDialog } from '@/features/spk/staking/dialogs/stake/useOpenStakeWithDelegateDialog'

export interface UseSparkPointsResult extends PointsData {
  userChainId: number
  openConnectModal: () => void
  openOnboardingDialog: () => void
  openReferralShareDialog: () => void
  openClaimDialog: () => void
  claimableSpk: NormalizedNumber
  claimedSpk: NormalizedNumber
  pointsSeason: number
  previousPointsSeason: number
  convertPointsPanelAction: 'claim' | 'switch-to-mainnet'
  showSparkLendBanner: boolean
  onSwitchToMainnet: () => void
  onBuy: () => void
}

export function useSparkPoints(): UseSparkPointsResult {
  const { account, walletMode } = useConnectedAddress({ throwOnNotConnected: false })
  const { isInSandbox, sandboxChainId } = useSandbox()
  const wagmiConfig = useConfig()
  const chainId = useChainId()
  const { referralCode } = useParams({ from: '/points/{-$referralCode}' })

  const rewardChainId = isInSandbox && sandboxChainId ? sandboxChainId : mainnet.id

  const chainConfig = getChainConfigEntry(rewardChainId)
  const pointsConfig = chainConfig.points
  assert(pointsConfig, 'Points config not available for this chain')
  const spkToken = chainConfig.tokenDefinitionRepository.findOneBySymbol(TokenSymbol('SPK'))

  const { openConnectModal = () => {} } = useConnectModal()
  const { switchChain } = useSwitchChain()
  const openDialog = useOpenDialog()
  const closeDialog = useCloseDialog()
  const { isVisible: isOnboardingDialogVisible } = useVisibility(sparkPointsOnboardingDialogId)
  const [convertPointsRewardQueryResult, pointsDataQueryResult] = useSuspenseQueries({
    queries: [
      {
        queryKey: convertPointsRewardQueryKey({ account, chainId: rewardChainId }),
        queryFn: () => convertPointsRewardQueryFunction({ account, wagmiConfig, chainId: rewardChainId, spkToken }),
      },
      {
        queryKey: pointsDataQueryKey({ account }),
        queryFn: () => pointsDataQueryFunction({ account }),
      },
    ],
  })
  const openStakeDialog = useOpenStakeWithDelegateDialog()

  const pointsData = pointsDataQueryResult.data
  const convertPointsReward = convertPointsRewardQueryResult.data

  const { agreedToTermsOfService } = useTermsOfService()
  const openedDialog = useOpenedDialog()

  useAppendBenmoRefCode()
  useEffect(() => {
    const isDialogOpen = openedDialog === pointsOnboardingDialogConfig.name

    if (!account) {
      isDialogOpen && closeDialog()
      return
    }

    const shouldOpenDialog =
      !pointsData.inviterCode &&
      isOnboardingDialogVisible &&
      agreedToTermsOfService(account) &&
      walletMode !== 'watch-wallet' &&
      !isInSandbox
    if (shouldOpenDialog && !isDialogOpen) {
      openDialog(pointsOnboardingDialogConfig, {})
      return
    }

    if (!shouldOpenDialog && isDialogOpen) {
      closeDialog()
    }
  }, [
    pointsData.inviterCode,
    account,
    openDialog,
    closeDialog,
    isOnboardingDialogVisible,
    agreedToTermsOfService,
    openedDialog,
    walletMode,
    isInSandbox,
  ])

  function openReferralShareDialog(): void {
    openDialog(referralShareDialogConfig, {})
    trackAction({
      name: 'Points_Invite',
      isInSandbox,
    })
  }

  function onBuy(): void {
    trackAction({
      name: 'Points_Buy',
      isInSandbox,
    })
  }

  function openClaimDialog(): void {
    openDialog(convertPointsDialogConfig, {
      onSuccess: () => {
        openStakeDialog({
          prefillWithMax: true,
          successViewProceedText: 'Back to Points',
        })
      },
    })
  }

  const claimableSpk = convertPointsReward?.reward
    ? convertPointsReward.reward.cumulativeAmount.minus(convertPointsReward.reward.preClaimed)
    : NormalizedNumber.ZERO
  const claimedSpk = convertPointsReward?.totalClaimed ?? NormalizedNumber.ZERO

  const showSparkLendBanner = referralCode === benmoInviteCode

  const pointsSeason = resolveActivePointsSeason()
  const previousPointsSeason = selectSeasonValue(pointsSeason, {
    2: 1,
    3: 2,
  })

  return {
    ...pointsData,
    userChainId: chainId,
    pointsSeason,
    previousPointsSeason,
    claimableSpk,
    claimedSpk,
    openConnectModal,
    openOnboardingDialog: () => openDialog(pointsOnboardingDialogConfig, {}),
    openReferralShareDialog,
    onBuy,
    openClaimDialog,
    convertPointsPanelAction: rewardChainId === chainId ? 'claim' : 'switch-to-mainnet',
    onSwitchToMainnet: () => switchChain({ chainId: mainnet.id }),
    showSparkLendBanner,
  }
}
